import { createContext, useContext, useState, ReactNode } from 'react';
import { useAuth } from '@/hooks/use-auth';
import { useUsage } from '@/hooks/use-usage';
import type { AIServiceLevel } from '@/lib/credit-config';

interface AIModeContextType {
  /** Whether AI features are currently enabled (toggle ON + paid tier) */
  aiEnabled: boolean;
  /** Whether the user's tier supports AI at all */
  tierSupportsAI: boolean;
  /** Current tier */
  tier: 'free' | 'pro' | 'enterprise';
  /** User's manual toggle preference */
  aiToggle: boolean;
  /** Toggle AI mode on/off (only works for paid tiers) */
  setAiToggle: (on: boolean) => void;
  /** Current AI service level (standard or premium) */
  serviceLevel: AIServiceLevel;
  /** Set the AI service level */
  setServiceLevel: (level: AIServiceLevel) => void;
  /** Whether the user can access premium AI */
  canUsePremium: boolean;
  /** Guard an AI action — returns true if allowed, shows upgrade prompt if not */
  guardAI: (actionLabel?: string) => boolean;
  /** Whether upgrade prompt is showing */
  showUpgrade: boolean;
  /** Dismiss upgrade prompt */
  dismissUpgrade: () => void;
  /** The action that triggered the upgrade prompt */
  blockedAction: string | null;
}

const AIModeContext = createContext<AIModeContextType>({
  aiEnabled: false,
  tierSupportsAI: false,
  tier: 'free',
  aiToggle: true,
  setAiToggle: () => {},
  serviceLevel: 'standard',
  setServiceLevel: () => {},
  canUsePremium: false,
  guardAI: () => false,
  showUpgrade: false,
  dismissUpgrade: () => {},
  blockedAction: null,
});

export function AIModeProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const { data: usage } = useUsage();
  const [aiToggle, setAiToggle] = useState(true);
  const [serviceLevel, setServiceLevel] = useState<AIServiceLevel>('standard');
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [blockedAction, setBlockedAction] = useState<string | null>(null);

  const tier = (usage?.tier ?? 'free') as 'free' | 'pro' | 'enterprise';
  const tierSupportsAI = tier !== 'free';
  const aiEnabled = tierSupportsAI && aiToggle && !!user;
  const canUsePremium = tier === 'enterprise';

  // Force standard if not enterprise
  const effectiveServiceLevel = canUsePremium ? serviceLevel : 'standard';

  function guardAI(actionLabel?: string): boolean {
    if (aiEnabled) return true;
    if (!tierSupportsAI) {
      setBlockedAction(actionLabel ?? 'This feature');
      setShowUpgrade(true);
    }
    return false;
  }

  function dismissUpgrade() {
    setShowUpgrade(false);
    setBlockedAction(null);
  }

  return (
    <AIModeContext.Provider
      value={{
        aiEnabled,
        tierSupportsAI,
        tier,
        aiToggle,
        setAiToggle,
        serviceLevel: effectiveServiceLevel,
        setServiceLevel,
        canUsePremium,
        guardAI,
        showUpgrade,
        dismissUpgrade,
        blockedAction,
      }}
    >
      {children}
    </AIModeContext.Provider>
  );
}

export function useAIMode() {
  return useContext(AIModeContext);
}
